import { createAction, handleActions } from 'redux-actions'
import type { AccessToken, Error, PushNotificationToken } from 'types/common'
import { Screens, Consents, FetchStates } from 'constants/common'
import type { ApplicationData } from 'services/api'

export type ApplicationDataState =
  | {
      fetchState: FetchStates.NOT_STARTED | FetchStates.PENDING
    }
  | {
      fetchState: FetchStates.SUCCESS
      data: ApplicationData
    }
  | {
      fetchState: FetchStates.FAILURE
      error: Error
    }

export type GeneralState<ConsentT = any> = {
  data: {
    accessToken: AccessToken
    pushNotificationToken?: PushNotificationToken
    activeScreen: Screens
    error?: Error
    devCode: string
    applicationData: ApplicationDataState
    consent: {
      type?: Consents
      details: ConsentT
    }
    consentFetchState: FetchStates
    authCheckState: FetchStates
    pushNotificationTokenFetchState: FetchStates
  }
}

const initialState: GeneralState = {
  data: {
    accessToken: '',
    pushNotificationToken: undefined,
    activeScreen: Screens.CREDENTIALS,
    error: undefined,
    devCode: '',
    applicationData: {
      fetchState: FetchStates.NOT_STARTED,
    },
    consent: {
      type: undefined,
      details: {},
    },
    consentFetchState: FetchStates.NOT_STARTED,
    authCheckState: FetchStates.NOT_STARTED,
    pushNotificationTokenFetchState: FetchStates.NOT_STARTED,
  },
}

export const setAccessTokenAction = createAction<AccessToken>('/general/SET_ACCESS_TOKEN')
export const setPushNotificationTokenAction = createAction<PushNotificationToken>(
  '/general/SET_PUSH_NOTIFICATION_TOKEN_ACTION',
)
export const setActiveScreenAction = createAction<string>('/general/SET_ACTIVE_SCREEN')
export const setConsentFetchingAction = createAction('/general/FETCH_CONSENT')
export const setAuthCheckingAction = createAction('/general/CHECK_AUTH')
export const setPushNotificationTokenFetchingAction = createAction(
  '/general/FETCH_PUSH_NOTIFICATION_TOKEN',
)
export const setConsentAction = createAction<{ type: string; details: object }>(
  '/general/SET_CONSENT',
)
export const setErrorAction = createAction<Error>('/general/SET_ERROR')
export const setApplicationDataAction = createAction<ApplicationData>(
  '/general/SET_APP_DATA',
)
export const setApplicationDataFetchingAction = createAction('/general/FETCH_APP_DATA')
export const setApplicationDataFetchFailedAction = createAction(
  '/general/FETCH_APP_DATA_FAILED',
)

export const generalReducer = handleActions<GeneralState, any>(
  {
    [setAccessTokenAction as any]: (state, action) => ({
      ...state,
      data: { ...state.data, accessToken: action.payload },
    }),
    [setPushNotificationTokenAction as any]: (state, action) => ({
      ...state,
      data: { ...state.data, pushNotificationToken: action.payload },
    }),
    [setActiveScreenAction as any]: (state, action) => ({
      ...state,
      data: { ...state.data, activeScreen: action.payload },
    }),
    [setConsentFetchingAction as any]: (state) => ({
      ...state,
      data: { ...state.data, consentFetchState: FetchStates.PENDING },
    }),
    [setAuthCheckingAction as any]: (state, action) => ({
      ...state,
      data: { ...state.data, authCheckState: action.payload },
    }),
    [setPushNotificationTokenFetchingAction as any]: (state, action) => ({
      ...state,
      data: { ...state.data, pushNotificationTokenFetchState: action.payload },
    }),
    [setConsentAction as any]: (state, action) => ({
      ...state,
      data: {
        ...state.data,
        consent: action.payload,
        consentFetchState: FetchStates.SUCCESS,
      },
    }),
    [setErrorAction as any]: (state, action) => ({
      ...state,
      data: { ...state.data, error: action.payload },
    }),
    [setApplicationDataAction as any]: (state, action) => ({
      ...state,
      data: {
        ...state.data,
        applicationData: {
          fetchState: FetchStates.SUCCESS,
          data: action.payload,
        },
      },
    }),
    [setApplicationDataFetchingAction as any]: (state) => ({
      ...state,
      data: {
        ...state.data,
        applicationData: {
          fetchState: FetchStates.PENDING,
        },
      },
    }),
    [setApplicationDataFetchFailedAction as any]: (state, action) => ({
      ...state,
      data: {
        ...state.data,
        applicationData: {
          fetchState: FetchStates.FAILURE,
          error: action.payload,
        },
      },
    }),
  },
  initialState,
)
